import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Image, Toast } from 'antd-mobile-v5'
import Tag from '../BuyCards/components/ChanghuanTag'
import styles from './ClubVipChangeEcards.module.css'
import { getCardFaceImageUrl } from '../../services/utilities'
import {
  useBindingEcards,
  useCancelBindingEcards,
  useClubVipFuiouPay,
  useClubVipPaymentStatus,
  useEcards,
  useGetVipEcardsReplacementOptions,
} from '../../services/hooks'
import { useClubVipPayment } from '../../services/useClubVipPayment'
import LoadingStatus from '../../components/FormTools/Loading'
import { allEcards } from '../../utils/cert'
import { showToast } from '../../components/common/tools'
import { changeEcardActiveFalse, changeEcardActiveTrue, refreshEcards } from '../../actions'
import QrCodePay from '../../components/QrCodePay/QrCodePay'
import { getChangeRight } from '../../reducers/selectors'
import { getParameterByName } from '../../components/common/utils'

const ClubVipChangeEcards = () => {
  const dispatch = useDispatch()
  const { hasChangeRight } = useSelector((state) => ({
    hasChangeRight: getChangeRight(state),
  }))

  // 获取页面参数
  const pageVipId = useMemo(() => getParameterByName('pageVipId'), [])

  // 状态管理
  const [selectedImage, setSelectedImage] = useState(0)
  const [options, setOptions] = useState([])
  const [selectedCerts, setSelectedCerts] = useState([])

  // Hooks
  const { loading: ecardsLoading, data: ecardsData } = useEcards({
    onSuccess: (data) => {
      dispatch(refreshEcards(data))
      getEcardsOptions()
    },
    onError: () => {
      Toast.show({
        icon: 'fail',
        content: '加载证书失败，请稍后重试',
      })
    },
  })

  const { loading, run: getEcardsOptions } = useGetVipEcardsReplacementOptions({
    onSuccess: (data) => {
      const standardCard = { desc: '普通版', imgFile: 'cert_161.jpg' }
      // 过滤掉生肖卡开头的数据
      if (data.images) {
        const filteredImages = data.images.filter((image) => !image.desc || !image.desc.startsWith('生肖卡'))
        setOptions([...filteredImages, standardCard])
      } else {
        setOptions([standardCard])
      }
    },
    onError: () => {
      Toast.show({
        icon: 'fail',
        content: '加载卡面选项失败，请稍后重试',
      })
      // 加载失败时设置默认卡面
      setOptions([{ desc: '普通版', imgFile: 'cert_161.jpg' }])
    },
  })

  const { run: bindEcards, loading: bindLoading } = useBindingEcards({
    onSuccess: (data) => {
      if (data) {
        showToast('卡面更换成功！')
        dispatch(changeEcardActiveTrue(data))
      }
    },
    onError: () => {
      Toast.show({
        icon: 'fail',
        content: '卡面更换失败，请稍后重试',
      })
    },
  })

  const { run: cancelBindEcards } = useCancelBindingEcards({
    onSuccess: (data) => {
      if (data) {
        showToast('卡面更换成功！')
        dispatch(changeEcardActiveFalse(data))
      }
    },
    onError: () => {
      Toast.show({
        icon: 'fail',
        content: '卡面更换失败，请稍后重试',
      })
    },
  })

  const { runAsync: fuiouPay } = useClubVipFuiouPay({})
  // 轮询失败时抑制 useRequestCommon 的全局错误 Modal（轮询类请求不弹窗，与 BooksCheckout/AgentElearning 一致）
  const { runAsync: checkFuiouStatus } = useClubVipPaymentStatus({ onError: () => true })

  // 计算属性
  const ecards = useMemo(() => allEcards(ecardsData), [ecardsData])
  const currentOption = useMemo(() => options[selectedImage], [options, selectedImage])

  // 获取图片路径
  const imgSrc = useMemo(() => {
    if (!currentOption) return ''

    return getCardFaceImageUrl(currentOption.imgFile, currentOption.desc)
  }, [currentOption])

  // 获取VIP类型
  const getVipType = useCallback(() => {
    switch (pageVipId) {
      case '1':
        return 'CLUB_VIP'
      case '2':
        return 'CLUB_LITE_VIP'
      case '3':
        return 'CLUB_PERIPHERY_VIP'
      default:
        return 'CLUB_LITE_VIP'
    }
  }, [pageVipId])

  // 获取VIP按钮文本
  const getVipButtonText = useCallback(() => {
    switch (pageVipId) {
      case '1':
        return '开通畅享课程会员'
      case '2':
        return '开通畅享卡面会员'
      case '3':
        return '开通畅享周边会员'
      default:
        return '立即开通会员'
    }
  }, [pageVipId])

  // 切换卡面
  const changeImage = useCallback((index) => {
    setSelectedCerts([])
    setSelectedImage(index)
  }, [])

  // 获取关联ID
  const getAffiliateId = useCallback((ecard) => {
    return ecard.affiliateCertificationId || ecard.affiliateCredentialId
  }, [])

  // 切换证书选择
  const toggleCert = useCallback(
    (ecard) => {
      if (!currentOption) return

      const ecardId = getAffiliateId(ecard)
      const hasCurrentCert = selectedCerts.find((cert) => cert.ecardId === ecardId)

      if (currentOption.desc === '普通版') {
        if (!hasCurrentCert) {
          setSelectedCerts((prev) => [...prev, { ecardId }])
        } else {
          setSelectedCerts((prev) => prev.filter((c) => c.ecardId !== ecardId))
        }
      } else {
        const hasBindEcard = currentOption.bindEcards?.find((card) => card.ecardId === ecardId)
        if (!hasBindEcard) {
          showToast('您当前选择的证书并无该卡面，请重新选择。', false)
          return
        }

        if (!hasCurrentCert) {
          const findSelectedEcard = currentOption.bindEcards.find((item) => item.ecardId === ecardId)
          if (findSelectedEcard) {
            setSelectedCerts((prev) => [...prev, findSelectedEcard])
          }
        } else {
          setSelectedCerts((prev) => prev.filter((c) => c.ecardId !== ecardId))
        }
      }
    },
    [currentOption, selectedCerts, getAffiliateId]
  )

  // 绑定卡面
  const bindingCards = useCallback(() => {
    if (selectedCerts.length === 0 || !currentOption) return

    if (currentOption.desc === '普通版') {
      cancelBindEcards(selectedCerts)
    } else {
      bindEcards(selectedCerts)
    }
  }, [selectedCerts, currentOption, bindEcards, cancelBindEcards])

  const { toPay, nativeQrCode, nativePrice, nativeCurrency, nativePayDisplay, setNativePayDisplay } = useClubVipPayment(
    {
      type: getVipType,
      fuiouPay,
      checkFuiouStatus,
    }
  )

  // 购买VIP
  const buyVip = useCallback(() => {
    toPay()
  }, [toPay])

  // 加载状态
  if (loading || ecardsLoading) {
    return <LoadingStatus />
  }

  return (
    <div className={styles.wrapper}>
      <QrCodePay
        visible={nativePayDisplay}
        setVisible={setNativePayDisplay}
        url={nativeQrCode}
        price={nativePrice}
        currency={nativeCurrency}
      />

      {/* 卡面预览 */}
      {options.length > 0 && currentOption && (
        <>
          <h1 className={styles['card-name']}>{currentOption.desc}</h1>
          <Image className={styles.card} src={imgSrc} alt={currentOption.desc} />
        </>
      )}

      {/* 卡面选择 */}
      <h4 className={styles['options-title']}>选择卡面</h4>
      {options.length > 0 ? (
        <div className={styles.tags}>
          {options.map((option, index) => (
            <Tag
              key={option.ecardImageId || index}
              selected={index === selectedImage}
              onClick={() => changeImage(index)}
            >
              {option.desc}
            </Tag>
          ))}
        </div>
      ) : (
        <div className={styles['no-options']}>暂无可更换的卡面</div>
      )}

      {/* 证书选择 */}
      <h4 className={styles['options-title']}>选择证书（可多选）</h4>
      {ecards.length > 0 ? (
        <div className={styles['certs-tags']}>
          {ecards.map((ecard) => (
            <Tag
              key={ecard.encodedId}
              selected={!!selectedCerts.find((c) => c.ecardId === getAffiliateId(ecard))}
              onClick={() => toggleCert(ecard)}
            >
              {ecard.ecardName}
            </Tag>
          ))}
        </div>
      ) : (
        <div className={styles['no-options']}>暂无可用证书</div>
      )}

      {/* 底部按钮 */}
      <div className={styles.bottom}>
        {hasChangeRight ? (
          ecards.length > 0 ? (
            <Button
              block
              className={styles.button}
              onClick={bindingCards}
              disabled={selectedCerts.length === 0}
              loading={bindLoading}
            >
              一键畅换
            </Button>
          ) : null
        ) : (
          <Button block className={styles.button} onClick={buyVip}>
            {getVipButtonText()}
          </Button>
        )}
        {!hasChangeRight && <div className={styles['bottom-text']}>请注意，您已购买证书的卡面仍然保留。</div>}
      </div>
    </div>
  )
}

export default ClubVipChangeEcards
